import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import SeriesCard from '../components/SeriesCard';
import Filters, { LANGUAGE_MAP } from '../components/Filters';
import Pagination from '../components/Pagination';
import MediaModal from '../components/MediaModal';
import { discoverSeries, fetchSeriesDetails } from '../services/tmdb';

// ========== TRANSLATIONS ==========
const translations = {
  en: {
    filterLabel: 'FILTERS',
    noFiltersApplied: 'No filters applied',
    loadingSeries: 'Loading series...',
    noSeriesFound: 'No series found with the selected filters',
    searchSeries: 'Search Series',
    genre: 'Genre',
    language: 'Language',
    firstAirYear: 'First Air Year',
    sortBy: 'Sort By',
    provider: 'Provider',
    allProviders: 'All Providers (Recommended)',
    allGenres: 'All Genres',
    allLanguages: 'All Languages',
    allYears: 'All Years',
    customLanguage: 'Custom',
    languagePlaceholder: 'Type language...',
    seriesGenres: {
      '': 'All Genres',
      '10759': 'Action & Adventure',
      '16': 'Animation',
      '35': 'Comedy',
      '80': 'Crime',
      '99': 'Documentary',
      '18': 'Drama',
      '10751': 'Family',
      '10762': 'Animation',
      '9648': 'Mystery',
      '10764': 'Reality',
      '10765': 'Sci-Fi & Fantasy',
      '10766': 'Soap',
      '10768': 'War & Politics',
      '37': 'Western',
    },
    languages: {
      '': 'All Languages',
      en: 'English',
      es: 'Spanish',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      ja: 'Japanese',
      ko: 'Korean',
      zh: 'Chinese',
      ru: 'Russian',
    },
    years: {
      '': 'All Years',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Present',
    },
    seriesSortOptions: {
      'popularity.desc': 'Popularity',
      'vote_count.desc': 'Box Office (Highest Grossing)',
      'lesser_known': 'Lesser Known',
    },
    providers: {
      '': 'All Providers',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '99': 'Shudder',
    },
  },
  fr: {
    filterLabel: 'FILTRES',
    noFiltersApplied: 'Aucun filtre appliqué',
    loadingSeries: 'Chargement des séries...',
    noSeriesFound: 'Aucune série trouvée avec les filtres sélectionnés',
    searchSeries: 'Rechercher des séries',
    genre: 'Genre',
    language: 'Langue',
    firstAirYear: 'Année de première diffusion',
    sortBy: 'Trier par',
    provider: 'Fournisseur',
    allProviders: 'Tous les fournisseurs',
    allGenres: 'Tous les genres',
    allLanguages: 'Toutes les langues',
    allYears: 'Toutes les années',
    customLanguage: 'Personnalisé',
    languagePlaceholder: 'Tapez une langue...',
    seriesGenres: {
      '': 'Tous les genres',
      '10759': 'Action & Aventure',
      '16': 'Animation',
      '35': 'Comédie',
      '80': 'Crime',
      '99': 'Documentaire',
      '18': 'Drame',
      '10751': 'Famille',
      '10762': 'Animation',
      '9648': 'Mystère',
      '10764': 'Téléréalité',
      '10765': 'Sci-Fi & Fantastique',
      '10766': 'Feuilleton',
      '10768': 'Guerre & Politique',
      '37': 'Western',
    },
    languages: {
      '': 'Toutes les langues',
      en: 'Anglais',
      es: 'Espagnol',
      fr: 'Français',
      de: 'Allemand',
      it: 'Italien',
      ja: 'Japonais',
      ko: 'Coréen',
      zh: 'Chinois',
      ru: 'Russe',
    },
    years: {
      '': 'Toutes les années',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Présent',
    },
    seriesSortOptions: {
      'popularity.desc': 'Populaire',
      'vote_count.desc': 'Box-office (plus gros revenus)',
      'lesser_known': 'Moins Connu',
    },
    providers: {
      '': 'Tous les fournisseurs',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '500': 'Shudder',
      '531': 'Paramount+',
    },
  },
  es: {
    filterLabel: 'FILTROS',
    noFiltersApplied: 'Sin filtros aplicados',
    loadingSeries: 'Cargando series...',
    noSeriesFound: 'No se encontraron series con los filtros seleccionados',
    searchSeries: 'Buscar series',
    genre: 'Género',
    language: 'Idioma',
    firstAirYear: 'Año de primera emisión',
    sortBy: 'Ordenar por',
    provider: 'Proveedor',
    allProviders: 'Todos los proveedores',
    allGenres: 'Todos los géneros',
    allLanguages: 'Todos los idiomas',
    allYears: 'Todos los años',
    customLanguage: 'Personalizado',
    languagePlaceholder: 'Escriba un idioma...',
    seriesGenres: {
      '': 'Todos los géneros',
      '10759': 'Acción y Aventura',
      '16': 'Animación',
      '35': 'Comedia',
      '80': 'Crimen',
      '99': 'Documental',
      '18': 'Drama',
      '10751': 'Familia',
      '10762': 'Animación',
      '9648': 'Misterio',
      '10764': 'Reality',
      '10765': 'Ciencia Ficción y Fantasía',
      '10766': 'Telenovela',
      '10768': 'Guerra y Política',
      '37': 'Western',
    },
    languages: {
      '': 'Todos los idiomas',
      en: 'Inglés',
      es: 'Español',
      fr: 'Francés',
      de: 'Alemán',
      it: 'Italiano',
      ja: 'Japonés',
      ko: 'Coreano',
      zh: 'Chino',
      ru: 'Ruso',
    },
    years: {
      '': 'Todos los años',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Presente',
    },
    seriesSortOptions: {
      'popularity.desc': 'Popular',
      'vote_count.desc': 'Taquilla (mayor recaudación)',
      'lesser_known': 'Menos Conocidos',
    },
    providers: {
      '': 'Todos los proveedores',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '500': 'Shudder',
      '531': 'Paramount+',
    },
  },
  it: {
    filterLabel: 'FILTRI',
    noFiltersApplied: 'Nessun filtro applicato',
    loadingSeries: 'Caricamento serie...',
    noSeriesFound: 'Nessuna serie trovata con i filtri selezionati',
    searchSeries: 'Cerca serie',
    genre: 'Genere',
    language: 'Lingua',
    firstAirYear: 'Anno di prima messa in onda',
    sortBy: 'Ordina per',
    provider: 'Fornitore',
    allProviders: 'Tutti i fornitori',
    allGenres: 'Tutti i generi',
    allLanguages: 'Tutte le lingue',
    allYears: 'Tutti gli anni',
    customLanguage: 'Personalizzato',
    languagePlaceholder: 'Digita una lingua...',
    seriesGenres: {
      '': 'Tutti i generi',
      '10759': 'Azione e Avventura',
      '16': 'Animazione',
      '35': 'Commedia',
      '80': 'Crimine',
      '99': 'Documentario',
      '18': 'Dramma',
      '10751': 'Famiglia',
      '10762': 'Animazione',
      '9648': 'Mistero',
      '10764': 'Reality',
      '10765': 'Fantascienza e Fantasy',
      '10766': 'Soap',
      '10768': 'Guerra e Politica',
      '37': 'Western',
    },
    languages: {
      '': 'Tutte le lingue',
      en: 'Inglese',
      es: 'Spagnolo',
      fr: 'Francese',
      de: 'Tedesco',
      it: 'Italiano',
      ja: 'Giapponese',
      ko: 'Coreano',
      zh: 'Cinese',
      ru: 'Russo',
    },
    years: {
      '': 'Tutti gli anni',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Presente',
    },
    seriesSortOptions: {
      'popularity.desc': 'Popolare',
      'vote_count.desc': 'Incasso (più alto)',
      'lesser_known': 'Meno Conosciuti',
    },
    providers: {
      '': 'Tutti i fornitori',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '500': 'Shudder',
      '531': 'Paramount+',
    },
  },
  nl: {
    filterLabel: 'FILTERS',
    noFiltersApplied: 'Geen filters toegepast',
    loadingSeries: 'Series laden...',
    noSeriesFound: 'Geen series gevonden met de geselecteerde filters',
    searchSeries: 'Zoek series',
    genre: 'Genre',
    language: 'Taal',
    firstAirYear: 'Eerste uitzendjaar',
    sortBy: 'Sorteer op',
    provider: 'Provider',
    allProviders: 'Alle providers',
    allGenres: 'Alle genres',
    allLanguages: 'Alle talen',
    allYears: 'Alle jaren',
    customLanguage: 'Aangepast',
    languagePlaceholder: 'Typ een taal...',
    seriesGenres: {
      '': 'Alle genres',
      '10759': 'Actie & Avontuur',
      '16': 'Animatie',
      '35': 'Komedie',
      '80': 'Misdaad',
      '99': 'Documentaire',
      '18': 'Drama',
      '10751': 'Familie',
      '10762': 'Animatie',
      '9648': 'Mysterie',
      '10764': 'Reality',
      '10765': 'Sci-Fi & Fantasy',
      '10766': 'Soap',
      '10768': 'Oorlog & Politiek',
      '37': 'Western',
    },
    languages: {
      '': 'Alle talen',
      en: 'Engels',
      es: 'Spaans',
      fr: 'Frans',
      de: 'Duits',
      it: 'Italiaans',
      ja: 'Japans',
      ko: 'Koreaans',
      zh: 'Chinees',
      ru: 'Russisch',
    },
    years: {
      '': 'Alle jaren',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Heden',
    },
    seriesSortOptions: {
      'popularity.desc': 'Populair',
      'vote_count.desc': 'Kassa (hoogste opbrengst)',
      'lesser_known': 'Minder Bekend',
    },
    providers: {
      '': 'Alle providers',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '500': 'Shudder',
      '531': 'Paramount+',
    },
  },
};

// ========== SESSION STORAGE HELPERS ==========
const getSessionValue = (key, defaultValue) => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored !== null ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const setSessionValue = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

// ========== CACHE HELPERS (localStorage, 12h TTL) ==========
const CACHE_KEY = 'series_default_cache';
const CACHE_TTL = 12 * 60 * 60 * 1000;

const getCachedSeries = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.timestamp > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data.series;
  } catch {
    return null;
  }
};

const setCachedSeries = (series) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      timestamp: Date.now(),
      series,
    }));
  } catch {
    // ignore
  }
};

const SeriesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const [currentPage, setCurrentPage] = useState(() => {
    const page = parseInt(searchParams.get('page'), 10);
    return isNaN(page) || page < 1 ? 1 : page;
  });
  const [totalPages, setTotalPages] = useState(1);

  const [genre, setGenre] = useState(() => searchParams.get('genre') || '');
  const [language, setLanguage] = useState(() => searchParams.get('language') || '');
  const [year, setYear] = useState(() => searchParams.get('year') || '');
  const [sort, setSort] = useState(() => searchParams.get('sort') || 'popularity.desc');
  const [provider, setProvider] = useState(() => searchParams.get('provider') || '');

  const [series, setSeries] = useState([]);
  const [filtersMinimized, setFiltersMinimized] = useState(true);
  const [loading, setLoading] = useState(true);

  // ----- Modal state (unified) -----
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ----- Ref to prevent duplicate modal openings -----
  const isOpeningRef = useRef(false);

  const filtersRef = useRef(null);
  const collapsedFiltersBtnRef = useRef(null);

  // Language
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  // Rating format (stars or number)
  const [ratingFormat, setRatingFormat] = useState(() => {
    return localStorage.getItem('ratingFormat') || 'stars';
  });

  // ---------- PAGINATION COOLDOWN ----------
  const [paginationCooldown, setPaginationCooldown] = useState(false);
  const cooldownTimeoutRef = useRef(null);

  const handlePaginationAction = useCallback((action) => {
    if (paginationCooldown) return;
    setPaginationCooldown(true);
    if (cooldownTimeoutRef.current) {
      clearTimeout(cooldownTimeoutRef.current);
    }
    cooldownTimeoutRef.current = setTimeout(() => {
      setPaginationCooldown(false);
      cooldownTimeoutRef.current = null;
    }, 2000);
    action();
  }, [paginationCooldown]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
      }
    };
  }, []);

  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  const tNested = (objKey, key) => {
    const langData = translations[lang] || translations.en;
    const obj = langData[objKey];
    return obj ? obj[key] : key;
  };

  // ----- Helper to detect custom language -----
  const getPredefinedLanguageCodes = () => {
    const langData = translations[lang] || translations.en;
    return Object.keys(langData.languages || {}).filter(k => k !== '');
  };

  const isLanguageCustom = (langCode) => {
    if (!langCode) return false;
    const predefined = getPredefinedLanguageCodes();
    return !predefined.includes(langCode);
  };

  // ===== Abort controller ref =====
  const abortControllerRef = useRef(null);

  // Listen for language & ratingFormat changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        setLang(e.newValue || 'en');
      }
      if (e.key === 'ratingFormat') {
        setRatingFormat(e.newValue || 'stars');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Sync URL with current filters and page
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (currentPage > 1) params.set('page', currentPage.toString());
    else params.delete('page');

    if (genre) params.set('genre', genre);
    else params.delete('genre');
    if (language) params.set('language', language);
    else params.delete('language');
    if (year) params.set('year', year);
    else params.delete('year');
    if (sort !== 'popularity.desc') params.set('sort', sort);
    else params.delete('sort');
    if (provider) params.set('provider', provider);
    else params.delete('provider');

    setSearchParams(params, { replace: true });
  }, [currentPage, genre, language, year, sort, provider, searchParams, setSearchParams]);

  // Scroll position persistence
  useEffect(() => {
    const saveScroll = () => {
      setSessionValue('seriesPage_scrollPos', window.scrollY);
    };
    window.addEventListener('beforeunload', saveScroll);
    return () => {
      saveScroll();
      window.removeEventListener('beforeunload', saveScroll);
    };
  }, []);

  const initialScrollRef = useRef(true);
  useEffect(() => {
    if (!loading && initialScrollRef.current) {
      initialScrollRef.current = false;
      const savedScroll = getSessionValue('seriesPage_scrollPos', 0);
      if (savedScroll > 0) {
        window.scrollTo(0, savedScroll);
      }
    }
  }, [loading]);

  // Reset page to 1 only when filters actually change
  const prevFilters = useRef({ genre, language, year, sort, provider });

  useEffect(() => {
    const prev = prevFilters.current;
    const changed =
      genre !== prev.genre ||
      language !== prev.language ||
      year !== prev.year ||
      sort !== prev.sort ||
      provider !== prev.provider;

    if (changed) {
      setCurrentPage(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    prevFilters.current = { genre, language, year, sort, provider };
  }, [genre, language, year, sort, provider]);

  const getActiveFiltersText = () => {
    const activeFilters = [];
    if (genre) {
      const genreName = tNested('seriesGenres', genre);
      if (genreName) activeFilters.push(genreName);
    }
    if (language) {
      let langName = tNested('languages', language);
      if (!langName || langName === language) {
        langName = LANGUAGE_MAP[language] || language;
      }
      if (langName) activeFilters.push(langName);
    }
    if (year) {
      const yearName = tNested('years', year);
      if (yearName) activeFilters.push(yearName);
    }
    if (sort) {
      const sortName = tNested('seriesSortOptions', sort);
      if (sortName) activeFilters.push(sortName);
    }
    if (provider) {
      const providerName = tNested('providers', provider);
      if (providerName) activeFilters.push(providerName);
    }
    return activeFilters.length > 0 ? activeFilters.join(' • ') : t('noFiltersApplied');
  };

  // ===== FETCH SERIES WITH ABORT AND CACHING =====
  const fetchSeries = useCallback(async () => {
    const isDefaultPage = currentPage === 1 && !genre && !language && !year && sort === 'popularity.desc' && !provider;

    // Try cache only for default page
    if (isDefaultPage) {
      const cached = getCachedSeries();
      if (cached) {
        setSeries(cached);
        setTotalPages(Math.min(500, 500));
        setLoading(false);
        return;
      }
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const isCustomLang = isLanguageCustom(language);
      const filters = { genre, language, year, sort, provider, isCustomLanguage: isCustomLang };
      const data = await discoverSeries(currentPage, filters, { signal: controller.signal });
      let results = data.results || [];

      // Fetch details for all pages (to show season count and years)
      const detailedResults = await Promise.all(
        results.map(async (seriesItem) => {
          try {
            const details = await fetchSeriesDetails(seriesItem.id);
            return {
              ...seriesItem,
              number_of_seasons: details.number_of_seasons || 0,
              first_air_date: details.first_air_date || seriesItem.first_air_date,
              last_air_date: details.last_air_date || null,
              first_air_year: details.first_air_date ? new Date(details.first_air_date).getFullYear() : null,
              last_air_year: details.last_air_date ? new Date(details.last_air_date).getFullYear() : null,
            };
          } catch {
            return {
              ...seriesItem,
              number_of_seasons: 0,
              first_air_year: seriesItem.first_air_date ? new Date(seriesItem.first_air_date).getFullYear() : null,
              last_air_year: null,
            };
          }
        })
      );

      if (isDefaultPage) {
        setCachedSeries(detailedResults);
      }
      setSeries(detailedResults);
      setTotalPages(Math.min(data.total_pages, 500));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching series:', error);
      }
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
        abortControllerRef.current = null;
      }
    }
  }, [currentPage, genre, language, year, sort, provider]);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  // ===== UI helpers =====
  const minimizeFilters = () => setFiltersMinimized(true);
  const expandFilters = () => setFiltersMinimized(false);

  const handleSearch = () => {
    minimizeFilters();
  };

  // ===== Modal functions =====
  const openContentModal = async (contentId, contentType) => {
    if (isOpeningRef.current) return;
    isOpeningRef.current = true;
    try {
      let details;
      if (contentType === 'movie') {
        const res = await fetch(
          `https://api.themoviedb.org/3/movie/${contentId}?api_key=57240db50c2008e78c261e1a934627e4&language=en-US`
        );
        details = await res.json();
        details.media_type = 'movie';
      } else if (contentType === 'tv') {
        details = await fetchSeriesDetails(contentId);
        details.media_type = 'tv';
      }
      if (details && details.id) {
        setSelectedMedia(details);
        setSelectedMediaType(contentType);
        setIsModalOpen(true);
        const params = new URLSearchParams(searchParams);
        if (contentType === 'movie') {
          params.set('movie', contentId);
          params.delete('series');
        } else {
          params.set('series', contentId);
          params.delete('movie');
        }
        setSearchParams(params, { replace: true });
      } else {
        const found = series.find(s => s.id === contentId);
        if (found) {
          setSelectedMedia({ ...found, media_type: 'tv' });
          setSelectedMediaType('tv');
          setIsModalOpen(true);
          const params = new URLSearchParams(searchParams);
          params.set('series', contentId);
          params.delete('movie');
          setSearchParams(params, { replace: true });
        } else {
          closeModal();
        }
      }
    } catch (error) {
      console.error('Error opening modal:', error);
      const found = series.find(s => s.id === contentId);
      if (found) {
        setSelectedMedia({ ...found, media_type: 'tv' });
        setSelectedMediaType('tv');
        setIsModalOpen(true);
        const params = new URLSearchParams(searchParams);
        params.set('series', contentId);
        params.delete('movie');
        setSearchParams(params, { replace: true });
      } else {
        closeModal();
      }
    } finally {
      isOpeningRef.current = false;
    }
  };

  const handleSeriesClick = (seriesId) => {
    if (!seriesId) return;
    openContentModal(seriesId, 'tv');
  };

  const handleMediaChange = (media, mediaType) => {
    setSelectedMedia(media);
    setSelectedMediaType(mediaType);
    const params = new URLSearchParams(searchParams);
    if (mediaType === 'movie') {
      params.set('movie', media.id);
      params.delete('series');
    } else {
      params.set('series', media.id);
      params.delete('movie');
    }
    setSearchParams(params, { replace: true });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMedia(null);
    setSelectedMediaType(null);
    const params = new URLSearchParams(searchParams);
    params.delete('movie');
    params.delete('series');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const movieId = searchParams.get('movie');
    const seriesId = searchParams.get('series');
    if (movieId && !isNaN(parseInt(movieId)) && !isOpeningRef.current) {
      openContentModal(parseInt(movieId), 'movie');
    } else if (seriesId && !isNaN(parseInt(seriesId)) && !isOpeningRef.current) {
      openContentModal(parseInt(seriesId), 'tv');
    }
  }, [searchParams]);

  return (
    <div className="page-content" id="seriesPage">
      {loading && (
        <div className="loading-overlay">
          <img src="/loading2.png" alt="Loading..." className="blinking-logo" />
        </div>
      )}

      {!loading && (
        <div className="series-content">
          <div
            id="collapsedSeriesFiltersBtn"
            className="collapsed-filters-btn"
            style={{ display: filtersMinimized ? 'flex' : 'none' }}
            ref={collapsedFiltersBtnRef}
            onClick={expandFilters}
          >
            <div className="filters-text">{t('filterLabel')}</div>
            <div className="active-filters-text">{getActiveFiltersText()}</div>
          </div>

          <div style={{ display: filtersMinimized ? 'none' : 'flex' }} ref={filtersRef}>
            <Filters
              type="series"
              onGenreChange={setGenre}
              onLanguageChange={setLanguage}
              onYearChange={setYear}
              onSortChange={setSort}
              onProviderChange={setProvider}
              onSearch={handleSearch}
              genreValue={genre}
              languageValue={language}
              yearValue={year}
              sortValue={sort}
              providerValue={provider}
              lang={lang}
              translations={translations}
            />
          </div>

          <div id="seriesGrid" className="series-grid">
            {series.length === 0 ? (
              <div className="empty-page" style={{ gridColumn: '1 / -1', padding: '50px' }}>
                <i className="fas fa-tv"></i>
                <p>{t('noSeriesFound')}</p>
              </div>
            ) : (
              series.map((seriesItem) => (
                <SeriesCard
                  key={seriesItem.id}
                  series={seriesItem}
                  onSeriesClick={handleSeriesClick}
                  firstAirYear={seriesItem.first_air_year}
                  lastAirYear={seriesItem.last_air_year}
                  numberOfSeasons={seriesItem.number_of_seasons}
                  ratingFormat={ratingFormat}
                />
              ))
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={() => handlePaginationAction(() => {
              if (currentPage > 1) {
                setCurrentPage(currentPage - 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                if (!filtersMinimized) minimizeFilters();
              }
            })}
            onNext={() => handlePaginationAction(() => {
              if (currentPage < totalPages) {
                setCurrentPage(currentPage + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                if (!filtersMinimized) minimizeFilters();
              }
            })}
            onPageChange={(page) => handlePaginationAction(() => {
              if (page >= 1 && page <= totalPages && page !== currentPage) {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                if (!filtersMinimized) minimizeFilters();
              }
            })}
            cooldown={paginationCooldown}
            type="series"
          />
        </div>
      )}

      {/* Unified Media Modal */}
      {isModalOpen && selectedMedia && (
        <MediaModal
          media={selectedMedia}
          mediaType={selectedMediaType}
          onClose={closeModal}
          onMediaChange={handleMediaChange}
        />
      )}
    </div>
  );
};

export default SeriesPage;